import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { Button } from '@/components/ui/Button';
import { Loader2 } from 'lucide-react';

export interface CancelPlayEventConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  /** Event detail page vs calendar session drawer */
  context?: 'event' | 'session';
  isSeriesSession?: boolean;
  isInProgress?: boolean;
  eventName?: string;
}

function getCancelDescription({
  context,
  isSeriesSession,
  isInProgress,
}: Pick<
  CancelPlayEventConfirmDialogProps,
  'context' | 'isSeriesSession' | 'isInProgress'
>): string {
  if (isInProgress) {
    return 'The draw will stop and court holds will be released. The event will disappear from the public board and play page. It stays on the operator calendar as cancelled. Online payments already collected are not refunded automatically.';
  }
  if (context === 'session' && isSeriesSession) {
    return 'This cancels only this date in the series. Other upcoming sessions stay scheduled unless you cancel them separately. Court holds are released. The session stays on the operator calendar as cancelled but is removed from the public play page and board.';
  }
  return 'Court holds are released. The event stays on the operator calendar as cancelled but is removed from the public play page and board. Online payments already collected are not refunded automatically.';
}

export function CancelPlayEventConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  context = 'event',
  isSeriesSession = false,
  isInProgress = false,
  eventName,
}: CancelPlayEventConfirmDialogProps) {
  const isSession = context === 'session';
  const title = isSession ? 'Cancel this session?' : 'Cancel this event?';
  const keepLabel = isSession ? 'Keep session' : 'Keep event';
  const confirmLabel = isSession ? 'Cancel session' : 'Cancel event';
  const loadingLabel = 'Cancelling…';

  return (
    <AlertDialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {eventName ? (
                <p>
                  <strong className="text-foreground">{eventName}</strong> will be cancelled.
                </p>
              ) : null}
              <p>{getCancelDescription({ context, isSeriesSession, isInProgress })}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={loading}>
            {keepLabel}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={loading}
            onClick={() => void onConfirm()}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                {loadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
